<script setup lang="ts">
import type { InputHTMLAttributes } from 'vue'

const t2 = useT2Base({
  Show: 'Rodyti',
  Hide: 'Slėpti',
  Password: 'Slaptažodis',
  'Must contain lowercase letter': 'Turi būti mažoji raidė',
  'Must contain uppercase letter': 'Turi būti didžioji raidė',
  'Must contain a number': 'Turi būti skaičius',
  'Must be at least 8 characters in length': 'Turi būti bent 8 simboliai',
})

type TextType = 'password:register' | 'password:login' | 'password:none'
type PassType = 'password' | 'text'

const emit = defineEmits<{ focus: [e: boolean] }>()
const props = defineProps<{
  label?: stringI18n
  disabled?: boolean
  type: TextType
}>()

const holder = defineModel<string | None>({ default: '' })
const elem = ref<HTMLInputElement>()
const data = shallowReactive({
  type: 'password' satisfies PassType as PassType,
  isFocused: false,
})
function focused(value: boolean) {
  emit('focus', value)
  data.isFocused = value
}

const validate: MyInputError<string>[] = [
  { msg: () => t2('Must contain lowercase letter'), isErr: (i) => !/(?=.*[a-z])/.test(i) },
  { msg: () => t2('Must contain uppercase letter'), isErr: (i) => !/(?=.*[A-Z])/.test(i) },
  // eslint-disable-next-line regexp/prefer-d
  { msg: () => t2('Must contain a number'), isErr: (i) => !/(?=.*[0-9])/.test(i) },
  // eslint-disable-next-line regexp/optimal-lookaround-quantifier
  { msg: () => t2('Must be at least 8 characters in length'), isErr: (i) => !/(?=.{8,})/.test(i) },
]

const myInput = useMyInput<string>({
  label: () => ui.value.label,
  optional: () => false,
  value: () => holder.value ?? '',
  validate: () => validate,
})
const error = useMyInputValidate(myInput, {
  focus: () => data.isFocused,
  disabled: () => props.disabled,
})

defineExpose({
  ...myInput.expose(),
  focus(): void {
    elem.value?.focus()
  },
})

const placeholder = '••••••••••••••••'
const attrMap: Record<TextType, InputHTMLAttributes> = {
  'password:login': { autocomplete: 'current-password' },
  'password:register': { autocomplete: 'new-password' },
  'password:none': { autocomplete: 'off' },
}

const attrs = useAttrs() as InputHTMLAttributes
const ui = computed(() => {
  const { type, label } = props
  return {
    label: label ?? t2('Password'),
    attrs: { ...attrs, ...((type && attrMap[type]) || attrMap['password:login']) },
  }
})
</script>

<template>
  <InputShellText :label="ui.label" :active="!!(holder || error)" :optional="false" :focus="data.isFocused" :labelid="myInput.id" :disabled :error>
    <template #default="{ classInput }">
      <input
        :id="myInput.id"
        ref="elem"
        v-model="holder"
        name="password"
        v-bind="ui.attrs"
        :type="data.type"
        :disabled
        :placeholder
        class="_pr6"
        :class="[classInput]"
        dir="ltr"
        tabindex="0"
        spellcheck="false"
        autocapitalize="off"
        @focus.passive="() => focused(true)"
        @blur.passive="() => focused(false)"
      />
    </template>

    <template #after>
      <div :class="$style.btn">
        <ButtonIcon
          v-memo="[data.type]"
          size="36"
          class="_bg0 _c2"
          :class="$style.btn2"
          :txt="data.type === 'password' ? t2('Show') : t2('Hide')"
          @click.passive="() => (data.type = data.type === 'password' ? 'text' : 'password')"
        >
          <IconEye :closed="data.type === 'password'" class="_svg20" />
        </ButtonIcon>
      </div>
    </template>
  </InputShellText>
</template>

<style module>
.btn2:hover {
  color: var(--black);
}
.btn {
  composes: global(_absolute);
  composes: global(_h100p);
  composes: global(_ctr);

  z-index: 1;
  top: 0;
  bottom: 0;
  right: 0;
  width: 60px;
}
</style>
